<template>
    <div>
        <v-card
            class="orders"
            :loading="busy ? 'accent' : null"
            outlined
        >
            <v-card-title class="page-heading primary">
                <v-row>
                    <v-col>
                        {{ !einvoiceOnly ? $tc('general.receipt', 2) : $tc('general.invoice', 2) }}
                    </v-col>
                    <v-col class="text-right">
                        <v-btn
                            class="mr-3"
                            dark
                            outlined
                            @click="importIncomingOrders"
                        >
                            <v-icon left>fal fa-upload</v-icon>
                            {{ $t('general.import') }}
                        </v-btn>
                        <v-btn
                            v-if="locale === 'sr'"
                            class="mr-3"
                            dark
                            outlined
                            @click="exportOrders"
                        >
                            <v-icon left>fal fa-download</v-icon>
                            {{ $t('general.export') }}
                        </v-btn>
                        <v-btn
                            class="minw100"
                            color="white"
                            outlined
                            @click="openIncomingOrderForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('order.add_incoming_order') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <order-list
                    ref="orderList"
                    :admin="true"
                    :users="users"
                    :clients="clients"
                    :cash-registers="cashRegisters"
                    @click="viewOrder"
                />
            </v-card-text>
        </v-card>

        <cod-dialog
            :show.sync="show.order"
            max-width="720px"
        >
            <!-- Header -->
            <template slot="header">
                <v-toolbar-title>
                    {{ options.viewOrder.title }}
                </v-toolbar-title>

                <v-spacer />

                <v-btn
                    v-for="(option, index) in options.viewOrder.primary"
                    :key="index"
                    text
                    dark
                    :disabled="option.disabled"
                    @click="option.onclick"
                >
                    <v-icon class="mr-3">{{ option.icon }}</v-icon>
                    {{ option.text }}
                </v-btn>

                <v-menu
                    v-if="options.viewOrder.secondary.length"
                    transition="slide-y-transition"
                    bottom
                >
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" icon>
                            <v-icon>far fa-ellipsis-v</v-icon>
                        </v-btn>
                    </template>

                    <v-list class="order-menu">
                        <v-list-item
                            v-for="(option, index) in options.viewOrder.secondary"
                            :key="index"
                            :disabled="option.disabled"
                            :class="option.classes"
                            @click="option.onclick"
                        >
                            <v-list-item-action class="mx-3">
                                <v-icon
                                    :class="option.classes"
                                    :disabled="option.disabled"
                                >
                                    {{ option.icon }}
                                </v-icon>
                            </v-list-item-action>
                            <v-list-item-title>
                                {{ option.text }}
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <div v-if="selectedOrder" slot="content">
                <v-slide-y-transition>
                    <div
                        v-if="initPrint && enableReceiptPrinter"
                        class="d-flex align-center flex-column"
                    >
                        <v-checkbox
                            v-model="useReceiptPrinter"
                            :label="$t('general.print_on_receipt_printer')"
                        />

                        <div>
                            <v-btn
                                class="mx-2"
                                color="primary"
                                outlined
                                @click="initPrint = false"
                            >
                                <v-icon class="mr-3">fal fa-times</v-icon>
                                {{ $t('general.cancel') }}
                            </v-btn>

                            <v-btn
                                class="mx-2"
                                color="primary"
                                depressed
                                @click="printOrder(selectedOrder, useReceiptPrinter)"
                            >
                                <v-icon class="mr-3">fal fa-check</v-icon>
                                {{ $t('general.print') }}
                            </v-btn>
                        </div>
                    </div>
                </v-slide-y-transition>

                <receipt
                    :order="selectedOrder"
                    :options="{
                        isReceipt: !(locale === 'sr' && selectedOrder.isUnpaid()),
                        isCopy: true,
                        printingSector: null,
                        forceInvoiceForm: forceInvoiceForm,
                    }"
                />
            </div>
        </cod-dialog>

        <!-- Import Receipts -->
        <cod-dialog :show.sync="show.importIncomingInstructions" max-width="610" >
            <template slot="header">
                <span>
                    {{ $t('orders.import_incoming_orders_heading') }}
                </span>
            </template>

            <div
                slot="content"
                class="pa-5"
            >
                <div class="mb-6">
                    <v-alert
                        prominent
                        type="info"
                        text
                    >
                        {{ $t('orders.import_incoming_orders_message') }}
                    </v-alert>
                </div>

                <div class="d-flex justify-center">
                    <qrcode
                        v-if="importToken"
                        :value="`${appUrl}/#/invoice-scan/${importToken}`"
                        :options="{
                            errorCorrectionLevel: 'L',
                            margin: 0,
                            scale: 6,
                            quality: 1
                        }"
                        tag="img"
                        :style="`width: 200px; image-rendering: pixelated;`"
                    />
                </div>

                <div class="d-flex justify-center mt-6">
                    {{ $t('orders.import_incoming_orders_message_alt') }}&nbsp;
                    <a href="#" @click="goToImportIncomingOrders">
                        {{ $t('general.here') }}
                    </a>
                </div>
            </div>
        </cod-dialog>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import Receipt from '@/components/print/Receipt';
import OrderList from '@/components/OrderList';
import AppService from '@/services/AppService';
import ApiService from '@/services/ApiService';
import EinvoiceService from '@/services/EinvoiceService';
import User from '@/models/User'
import Client from '@/models/Client'
import CashRegister from '@/models/CashRegister'

export default {
    components: {
        CodDialog,
        Receipt,
        OrderList
    },

    created() {
        loadData('/admin/load/orders').then(data => this.onDataLoaded(data));

        this.receiptPrinter = this.$printService.getAssignedPrinter('receipt');
        this.useReceiptPrinter = this.enableReceiptPrinter;

        this.appUrl = AppService.getAppUrl()
    },

    data() {
        return {
            busy: false,
            incoming: false,
            appUrl: null,
            importToken: null,
            show: {
                order: false,
                importIncomingInstructions: false,
            },

            selectedOrder: null,
            users: [],
            clients: [],
            cashRegisters: [],

            initPrint: false,
            useReceiptPrinter: true,
            receiptPrinter: null,

            forceInvoiceForm: false
        };
    },

    computed: {
        options() {
            const printOrder = {
                available: locale !== 'sr' || this.selectedOrder?.isEinvoice(),
                text: this.$t('general.print'),
                icon: 'fal fa-print',
                disabled: false,
                classes: {},
                onclick: this.togglePrint
            }

            const changeFormat = {
                available: this.selectedOrder && this.selectedOrder.isFiscal(),
                text: this.forceInvoiceForm ? this.$t('general.format_regular') : this.$t('general.format_wide'),
                icon: this.forceInvoiceForm ? 'fal fa-receipt' : 'fal fa-file',
                disabled: (locale === 'sr' && this.selectedOrder?.isUnpaid()) || this.selectedOrder?.isCreditNote(),
                classes: {},
                onclick: () => this.forceInvoiceForm = !this.forceInvoiceForm
            }

            const saveAsPdf = {
                available: this.selectedOrder && this.canExportReceipt,
                text: this.$t('general.save_as_pdf'),
                icon: 'fal fa-file-pdf',
                disabled: false,
                classes: {},
                onclick: this.saveAsPdf
            }

            const einvoiceApprove = {
                available: (this.selectedOrder?.einvoiceIsNew() || this.selectedOrder?.einvoiceIsSeen() || this.selectedOrder?.einvoiceIsRejected())
                    && this.selectedOrder?.isIncoming(),
                text: this.$t('einvoice.approve'),
                icon: 'fal fa-file-check',
                disabled: false,
                classes: { 'success--text': true },
                onclick: this.einvoiceApprove
            }

            const einvoiceReject = {
                available: (this.selectedOrder?.einvoiceIsNew() || this.selectedOrder?.einvoiceIsSeen()) && this.selectedOrder?.isIncoming(),
                text: this.$t('einvoice.reject'),
                icon: 'fal fa-file-times',
                disabled: false,
                classes: { 'error--text': true },
                onclick: this.einvoiceReject
            }

            const einvoiceStorno = {
                available: this.selectedOrder?.isEinvoice() && this.selectedOrder?.allowCancel(),
                text: this.$t('einvoice.storno'),
                icon: 'fal fa-times',
                disabled: false,
                classes: { 'error--text': true },
                onclick: this.einvoiceStorno
            }

            return {
                viewOrder: {
                    title: this.viewOrderTitle,
                    back: () => this.show.order = false,
                    primary: [
                        printOrder
                    ].filter(o => o.available),
                    secondary: [
                        einvoiceApprove,
                        einvoiceReject,
                        einvoiceStorno,
                        changeFormat,
                        saveAsPdf,
                    ].filter(o => o.available)
                },
            }
        },

        statuses() {

            return this.appConfig.order.statuses.map(status => {
                return {
                    value: status,
                    display: this.$t(`orders.status.${status}`),
                }
            })
        },

        viewOrderTitle() {
            if(!this.selectedOrder) {
                return ''
            }

            if(this.selectedOrder.isProforma()) {
                return this.$tc('general.proforma', 1)
            }

            if(this.selectedOrder.getPaidInvoice().toUnit() > 0) {
                return this.$tc('general.invoice_noncash', 1)
            }

            return this.$tc('general.receipt', 1)
        },

        enableReceiptPrinter() {
            return !!this.receiptPrinter && this.device.platform !== 'web'
        },

        canExportReceipt() {
            if(!this.selectedOrder) return false;

            if(locale === 'sr') {
                return this.selectedOrder.register_status === 'success'
                    && !!this.selectedOrder.register_request
                    && !!this.selectedOrder.register_response
            }

            return true;
        },

        einvoiceOnly() {
            return EinvoiceService.einvoiceOnly();
        }
    },

    methods: {
        onDataLoaded(data) {
            this.users = data.users.map(u => new User(u));
            this.clients = data.clients.map(c => new Client(c));
            this.cashRegisters = data.cash_registers.map(cr => new CashRegister(cr));
        },

        async viewOrder(order) {
            if(this.busy || !order.items.length) return;

            this.busy = true;
            this.selectedOrder = order;
            this.forceInvoiceForm = order.getPaidInvoice().toUnit() != 0
                || order.isProforma()
                || !order.isFiscal();
            this.initPrint = false;

            if(this.selectedOrder.isEinvoice()) {
                await this.selectedOrder.einvoiceRefresh();
            }

            this.busy = false;
            this.show.order = true;
        },

        togglePrint() {
            if(!this.enableReceiptPrinter) {
                this.printOrder(this.selectedOrder, false)
                return
            }

            if(this.device.platform === 'android') {
                this.printOrder(this.selectedOrder, true)
                return
            }

            this.initPrint = !this.initPrint
        },

        printOrder(order, useReceiptPrinter) {
            this.initPrint = false

            this.$printService.printReceipt(order, {
                isCopy: true,
                choosePrinter: !useReceiptPrinter,
                forceInvoiceForm: this.forceInvoiceForm,
            })
        },

        openIncomingOrderForm(item = null) {
            if(!item) {
                this.$router.push({ name: 'adminOrderCreate' }).catch(err => {});
                return;
            }

            this.$router.push({ name: 'adminOrderUpdate', params: { id: item.id } }).catch(err => {});
        },

        async exportOrders(){
            const startDate = `${this.$refs.orderList.form.startDate} ${this.$refs.orderList.form.startTime}`;
            const endDate = `${this.$refs.orderList.form.endDate} ${this.$refs.orderList.form.endTime}`;

            try {
                const response = await ApiService.get(`/admin/orders/export`, {
                    responseType: 'blob',
                    params: { start_date: startDate, end_date: endDate, for_company: 1 },
                    timeout: 120000
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', this.$tc('general.receipt', 2) + '.xlsx');
                document.body.appendChild(link);
                link.click();

            } catch (error) {
                error.response.data.text().then(errResponse => {
                    if(!errResponse) return;

                    const errors = JSON.parse(errResponse)?.errors;
                    const errorKeys = Object.keys(errors || {});
                    const errorMsg = errorKeys.length > 0
                        ? this.$t(`orders.errors.${errorKeys[0]}`)
                        : this.$t('general.action_failed');
                    this.$snackbar(errorMsg, 'error');
                })
            }
        },

        async importIncomingOrders() {
            const response = await ApiService.post('/admin/orders/import/enable', {});
            this.importToken = response.data.token;

            if (/Android|iPhone/i.test(navigator.userAgent)) {
                this.goToImportIncomingOrders();
            } else {
                this.show.importIncomingInstructions = true;
            }
        },

        goToImportIncomingOrders() {
            if(!this.importToken) return;
            this.$router.push({ name: 'invoiceScan',  params: { token: this.importToken }}).catch(err => {});
        },

        saveAsPdf() {
            if(!this.selectedOrder) return false;

            this.$printService.printReceipt(this.selectedOrder, {
                isCopy: true,
                choosePrinter: false,
                forceInvoiceForm: this.forceInvoiceForm,
                saveAsPdfFile: locale === 'sr' && this.selectedOrder.register_number
                    ? this.selectedOrder.register_number
                    : `${this.$tc('general.receipt', 1)}-${this.selectedOrder.number}`
            })
        },

        einvoiceApprove() {
            this.$confirm(
                this.$t('einvoice.approve_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.back'),
                    icon: 'fal fa-file-contract',
                    color: 'success',
                    title: this.$t('general.confirm'),
                },
                async result => {
                    if (!result) return;

                    const orderResult = await this.selectedOrder.$duplicate().einvoiceApprove({ user: this.user })

                    this.onOrderIssue(orderResult, this.$t('einvoice.approved'))
                }
            )
        },

        einvoiceReject() {
            const additionalOptions = {
                formFields: [{
                    type: 'text',
                    key: 'comment',
                    label: this.$t('einvoice.reject_enter_reason'),
                }],
            };

            this.$confirm(
                this.$t('einvoice.reject_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.back'),
                    icon: 'fal fa-file-contract',
                    color: 'error',
                    title: this.$t('general.confirm'),
                    ...additionalOptions
                },
                async result => {
                    if (!result.confirm) return;

                    const orderResult = await this.selectedOrder.$duplicate().einvoiceReject({
                        user: this.user,
                        comment: result.data.comment
                    })

                    this.onOrderIssue(orderResult, this.$t('einvoice.rejected'))
                }
            )
        },

        einvoiceStorno() {
            const additionalOptions = {
                formFields: [{
                    type: 'text',
                    key: 'comment',
                    label: this.$t('einvoice.storno_enter_reason'),
                }],
            };

            this.$confirm(
                this.$t('einvoice.storno_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.back'),
                    icon: 'fal fa-file-contract',
                    color: 'error',
                    title: this.$t('general.confirm'),
                    ...additionalOptions
                },
                async result => {
                    if (!result.confirm) return;

                    const orderResult = await this.selectedOrder.$duplicate().einvoiceStorno({
                        user: this.user,
                        comment: result.data.comment
                    })

                    this.onOrderIssue(orderResult, this.$t('einvoice.stornoed'))
                }
            )
        },

        /**
         * On order issue
         */
        onOrderIssue(result, successMsg) {
            if(result.status === Order.SYNC_STATUS_FAILED) {
                this.$confirm(result.error, {
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('order.error.title'),
                }, async result => {});

                return
            }

            if(result.status === Order.SYNC_STATUS_SUCCESS_WITH_ERRORS) {
                this.$snackbar(result.error || result.efiError || result.printError, 'warning', {
                    title: this.$t('order.issued'),
                    mode: 'multi-line',
                    timeout: 7000,
                })
            }

            if(result.status === Order.SYNC_STATUS_SUCCESS) {
                this.$snackbar(successMsg, 'success', { timeout: 3000 })
            }

            this.show.order = false;

            if(this.$refs.orderList) {
                this.$refs.orderList.getOrders()
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.orders {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }
}
</style>
